// ✅ THEME-ONLY UPDATE + NEW "Active Leaves" TILE (with revoke)
// - Keeps your existing Requests table business logic unchanged
// - Adds: Active Leaves tile below + revoke action + backend hooks

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Collapse,
  Chip,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";
import { formatDateTimeIST } from "./commonfunctions/dateUtils";

// ✅ Theme constants (same as your other pages)
const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";
const BORDER_HOVER = "rgba(25,118,210,0.45)";
const BORDER_FOCUS = "rgba(240,120,40,0.80)";
const ROW_ALT = "rgba(25,118,210,0.06)";
const ROW_HOVER = "rgba(25,118,210,0.10)";

const ApproverTimeOffRequest = () => {
  const navigate = useNavigate();

  // ✅ shared sx
  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  // ✅ Inputs/Selects
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const selectSx = {
    ...inputSx,
    "& .MuiSelect-select": { py: 1.05 },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    color: "white",
    borderColor: "rgba(255,255,255,0.70)",
    "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.12)" },
  };

  const dangerBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: "rgba(211,47,47,0.95)",
    "&:hover": { backgroundColor: "rgba(183,28,28,1)" },
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.75 },
  });

  // ===== state (existing, unchanged) =====
  const [data, setData] = useState([]);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);

  const [logincampusCode, setCampusCode] = useState("");
  const [err, setErr] = useState("");
  const [savingId, setSavingId] = useState(null);

  // ✅ NEW: active leaves tile state
  const [activeLeaves, setActiveLeaves] = useState([]);
  const [loadingActiveLeaves, setLoadingActiveLeaves] = useState(false);
  const [revokeId, setRevokeId] = useState(null);
  const [revokeComments, setRevokeComments] = useState({});

  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [historyByStudent, setHistoryByStudent] = useState({});
  const [loadingHistoryId, setLoadingHistoryId] = useState(null);

  // optional toast (keeps your alert() logic too)
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const token = localStorage.getItem("auth");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Extract CampusCode from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
    } catch (e) {
      console.error("Error decoding token:", e);
      setCampusCode("");
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  // Fetch all records (existing)
  const fetchRequests = async () => {
    if (!logincampusCode) return;
    setErr("");
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/approverTimeOffRequest`, {
        ...authHeader,
        params: { CampusCode: logincampusCode, ApprovalQueueCode: "TIMEOFF", },
      });
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) navigate("/");
      setErr("Failed to load time-off requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode, navigate, authHeader]);

  // ✅ NEW: fetch active leaves for tile
  const fetchActiveLeaves = async () => {
    if (!logincampusCode) return;
    setLoadingActiveLeaves(true);
    try {
      const resp = await axios.get(`${API_URL}/api/activeLeaves`, {
        ...authHeader,
        params: { CampusCode: logincampusCode },
      });
      setActiveLeaves(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      console.error("Error fetching active leaves:", e);
      if (e.response?.status === 401) navigate("/");
      setToast({ open: true, type: "error", msg: "Failed to load active leaves." });
      setActiveLeaves([]);
    } finally {
      setLoadingActiveLeaves(false);
    }
  };

  useEffect(() => {
    fetchActiveLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode, navigate, authHeader]);

  const pad2 = (n) => String(n).padStart(2, "0");

  const getUploadUrl = (value) => {
    if (!value) return "";
    if (value.startsWith("blob:") || value.startsWith("http")) return value;
    return `${API_UPLOAD_URL}/uploads/${value}`;
  };

  const AccompanyingPhotoThumb = ({ src, label = "Photo" }) => {
    const imgSrc = getUploadUrl(src);

    if (!imgSrc) {
      return (
        <Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
          -
        </Typography>
      );
    }

    return (
      <Tooltip
        arrow
        placement="right"
        title={
          <Box
            component="img"
            src={imgSrc}
            alt={label}
            sx={{
              width: 160,
              height: 190,
              objectFit: "cover",
              borderRadius: 1,
            }}
          />
        }
      >
        <Box
          component="img"
          src={imgSrc}
          alt={label}
          sx={{
            width: 34,
            height: 40,
            objectFit: "cover",
            borderRadius: 1,
            border: `1px solid ${BORDER}`,
            cursor: "pointer",
            backgroundColor: "rgba(25,118,210,0.04)",
          }}
        />
      </Tooltip>
    );
  };

  // For <input type="datetime-local"> needs "YYYY-MM-DDTHH:mm"
  const toLocalInputValue = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
      d.getHours()
    )}:${pad2(d.getMinutes())}`;
  };

  const handleFieldChange = (TimeOffRequestID, field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [TimeOffRequestID]: {
        ...prev[TimeOffRequestID],
        [field]: value,
      },
    }));
  };

  const handleSave = async (TimeOffRequestID) => {
    const row = data.find((item) => item.TimeOffRequestID === TimeOffRequestID);
    const updatedFields = editedData[TimeOffRequestID] || {};
  
    const finalStatus = updatedFields.LeaveStatus || row?.LeaveStatus || "Pending";
  
    if (finalStatus === "Pending") {
      setToast({
        open: true,
        type: "error",
        msg: "Please choose Approved or Rejected before saving.",
      });
      return;
    }
  
    const payload = {
      TimeOffRequestID,
      CampusCode: logincampusCode,
      ApprovalQueueCode: row?.ApprovalQueueCode || "TIMEOFF",
      ActionTaken: finalStatus === "Approved" ? "APPROVED" : "REJECTED",
      Comments: updatedFields.Comments ?? row?.Comments ?? "",
      ApprovedLeaveStartDate:
        updatedFields.ApprovedLeaveStartDate ||
        row?.ApprovedLeaveStartDate ||
        row?.LeaveStartDate,
      ApprovedLeaveEndDate:
        updatedFields.ApprovedLeaveEndDate ||
        row?.ApprovedLeaveEndDate ||
        row?.LeaveEndDate,
      ActionBy: localStorage.getItem("username") || "",
    };
  
    try {
      setSavingId(TimeOffRequestID);
  
      const resp = await axios.put(
        `${API_URL}/api/timeoff/approval/action`,
        payload,
        authHeader
      );
  
      if (resp.status === 200) {
        setToast({
          open: true,
          type: "success",
          msg:
            finalStatus === "Approved"
              ? "Approval step completed."
              : "Request rejected.",
        });
  
        setEditedData((prev) => {
          const { [TimeOffRequestID]: _removed, ...rest } = prev;
          return rest;
        });
  
        fetchRequests();
        fetchActiveLeaves();
      }
    } catch (e) {
      console.error("Approval action failed:", e);
      setToast({
        open: true,
        type: "error",
        msg: e.response?.data?.message || "Approval action failed.",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleRevokeCommentChange = (timeOffRequestId, value) => {
  setRevokeComments((prev) => ({
    ...prev,
    [timeOffRequestId]: value,
  }));
  };

  const handleRevokeLeave = async (TimeOffRequestID) => {
  const commentRaw = revokeComments[TimeOffRequestID] || "";
  const comment = commentRaw.trim();

  if (!comment) {
    setToast({ open: true, type: "error", msg: "Revocation comments are mandatory." });
    return;
  }

  const yes = window.confirm(
    `Revoke this active leave (ID: ${TimeOffRequestID})?\n\nComment: ${comment}\n\nThis will immediately invalidate the active leave.`
  );
  if (!yes) return;

  try {
    setRevokeId(TimeOffRequestID);
    setToast({ open: false, type: "success", msg: "" });

    const payload = {
      CampusCode: logincampusCode,
      TimeOffRequestID,
      RevokedBy: localStorage.getItem("username") || "",
      RevocationComments: comment, // ✅ mandatory
    };

    const resp = await axios.put(`${API_URL}/api/revokeActiveLeave`, payload, authHeader);

    if (resp.status === 200) {
      setToast({ open: true, type: "success", msg: "Leave revoked successfully." });

      // Clear comment for that row
      setRevokeComments((prev) => {
        const { [TimeOffRequestID]: _removed, ...rest } = prev;
        return rest;
      });

      // Refresh tile + main list
      fetchActiveLeaves();
      fetchRequests();
    } else {
      setToast({ open: true, type: "error", msg: "Failed to revoke leave." });
    }
  } catch (e) {
    console.error("Revoke failed:", e);
    setToast({
      open: true,
      type: "error",
      msg: e.response?.data?.message || "Revoke failed.",
    });
    if (e.response?.status === 401) navigate("/");
  } finally {
    setRevokeId(null);
  }
};

const fetchLeaveHistoryForStudent = async (item) => {
  const requestId = item.TimeOffRequestID;
  const studentId = item.ID || item.StudentID;

  if (!studentId || !logincampusCode) return;

  if (expandedRequestId === requestId) {
    setExpandedRequestId(null);
    return;
  }

  setExpandedRequestId(requestId);

  if (historyByStudent[studentId]) return;

  try {
    setLoadingHistoryId(requestId);

    const resp = await axios.get(`${API_URL}/api/dataTimeOffRequestManage`, {
      ...authHeader,
      params: {
        search: studentId,
        CampusCode: logincampusCode,
      },
    });

    setHistoryByStudent((prev) => ({
      ...prev,
      [studentId]: Array.isArray(resp.data) ? resp.data : [],
    }));
  } catch (e) {
    console.error("Failed to load leave history:", e);
    setToast({
      open: true,
      type: "error",
      msg: "Failed to load student leave history.",
    });
  } finally {
    setLoadingHistoryId(null);
  }
};



  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Approver Time Off Request
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Review requests, adjust approved dates, and update status
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={() => {
              fetchRequests();
              fetchActiveLeaves();
            }}
            disabled={loading || loadingActiveLeaves}
            sx={outlinedBtnSx}
          >
            {(loading || loadingActiveLeaves) ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              "Reload"
            )}
          </Button>
        </Box>

        {/* ✅ Body */}
        <Box sx={bodySx}>
          {err && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(25,118,210,0.18)",
              }}
            >
              {err}
            </Alert>
          )}

          {(loading || loadingActiveLeaves) && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: "rgba(25,118,210,0.10)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: THEME_ORANGE,
                    borderRadius: 999,
                  },
                }}
              />
            </Box>
          )}

          {/* ✅ Requests section (stripe) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Requests
              </Typography>

              {/* OUTER – keeps rounded corners & spacing */}
              <Paper elevation={0} sx={tableWrapSx}>
                {/* INNER – ONLY for horizontal scroll */}
                <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                  <TableContainer component="div">
                    {loading ? (
                      <Typography sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                        Loading...
                      </Typography>
                    ) : data.length === 0 ? (
                      <Typography sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                        No records found
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            {[
                              "#",
                              "ID",
                              "Full Name",
                              "Mobile",
                              "Campus",
                              "Requested Leave Start",
                              "Requested Leave End",
                              "Approved Leave Start",
                              "Approved Leave End",
                              "Leave Days",
                              "Leave Type",
                              "Reason",
                              "Comments",
                              "Leave Status",
                              "Pending Approval",
                              "Last Approver",
                              "Actions",
                            ].map((h) => (
                              <TableCell key={h} sx={{ whiteSpace: "nowrap" }}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                          
                        <TableBody>
                          {data.map((item, idx) => {
                            const studentId = item.ID || item.StudentID;
                            const history = historyByStudent[studentId] || [];
                            const isExpanded = expandedRequestId === item.TimeOffRequestID;
                          
                            return (
                              <React.Fragment key={item.TimeOffRequestID}>
                                <TableRow
                                  sx={{ ...zebraRowSx(idx), cursor: "pointer" }}
                                  onClick={() => fetchLeaveHistoryForStudent(item)}
                                >
                                  <TableCell>{idx + 1}</TableCell>
                            
                                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 900 }}>
                                    {studentId}
                                  </TableCell>
                            
                                  <TableCell>{item.FullName}</TableCell>
                                  <TableCell>{item.MobileNumber}</TableCell>
                                  <TableCell>{item.SubCampusCode}</TableCell>
                            
                                  <TableCell>{formatDateTimeIST(item.LeaveStartDate)}</TableCell>
                                  <TableCell>{formatDateTimeIST(item.LeaveEndDate)}</TableCell>
                            
                                  <TableCell sx={{ minWidth: 190 }} onClick={(e) => e.stopPropagation()}>
                                    <TextField
                                      size="small"
                                      type="datetime-local"
                                      sx={inputSx}
                                      value={
                                        editedData[item.TimeOffRequestID]?.ApprovedLeaveStartDate ??
                                        toLocalInputValue(item.ApprovedLeaveStartDate) ??
                                        toLocalInputValue(item.LeaveStartDate)
                                      }
                                      onChange={(e) =>
                                        handleFieldChange(
                                          item.TimeOffRequestID,
                                          "ApprovedLeaveStartDate",
                                          e.target.value
                                        )
                                      }
                                      InputLabelProps={{ shrink: true }}
                                      fullWidth
                                    />
                                  </TableCell>
                                    
                                  <TableCell sx={{ minWidth: 190 }} onClick={(e) => e.stopPropagation()}>
                                    <TextField
                                      size="small"
                                      type="datetime-local"
                                      sx={inputSx}
                                      value={
                                        editedData[item.TimeOffRequestID]?.ApprovedLeaveEndDate ??
                                        toLocalInputValue(item.ApprovedLeaveEndDate) ??
                                        toLocalInputValue(item.LeaveEndDate)
                                      }
                                      onChange={(e) =>
                                        handleFieldChange(
                                          item.TimeOffRequestID,
                                          "ApprovedLeaveEndDate",
                                          e.target.value
                                        )
                                      }
                                      InputLabelProps={{ shrink: true }}
                                      fullWidth
                                    />
                                  </TableCell>
                                    
                                  <TableCell>{item.LeaveDays}</TableCell>
                                  <TableCell>{item.LeaveType}</TableCell>
                                  <TableCell sx={{ minWidth: 220 }}>{item.ReasonForLeave}</TableCell>
                                    
                                  <TableCell sx={{ minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                                    <TextField
                                      size="small"
                                      sx={inputSx}
                                      value={
                                        editedData[item.TimeOffRequestID]?.Comments ??
                                        item.Comments ??
                                        ""
                                      }
                                      onChange={(e) =>
                                        handleFieldChange(
                                          item.TimeOffRequestID,
                                          "Comments",
                                          e.target.value
                                        )
                                      }
                                      fullWidth
                                    />
                                  </TableCell>
                                    
                                  <TableCell sx={{ minWidth: 160 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      sx={selectSx}
                                      value={
                                        editedData[item.TimeOffRequestID]?.LeaveStatus ||
                                        item.LeaveStatus ||
                                        "Pending"
                                      }
                                      onChange={(e) =>
                                        handleFieldChange(
                                          item.TimeOffRequestID,
                                          "LeaveStatus",
                                          e.target.value
                                        )
                                      }
                                      fullWidth
                                    >
                                      <MenuItem value="Pending">Pending</MenuItem>
                                      <MenuItem value="Approved">Approved</MenuItem>
                                      <MenuItem value="Rejected">Rejected</MenuItem>
                                    </Select>
                                  </TableCell>
                                    
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={item.CurrentApprovalStepName}
                                      sx={{ fontWeight: 900 }}
                                    />
                                  </TableCell>
                                  
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={
                                        item.LastApprovedBy || "-"
                                      }
                                      sx={{ fontWeight: 900 }}
                                    />
                                  </TableCell>
                                  
                                    
                                  <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      variant="contained"
                                      onClick={() => handleSave(item.TimeOffRequestID)}
                                      disabled={
                                        !editedData[item.TimeOffRequestID] ||
                                        savingId === item.TimeOffRequestID
                                      }
                                      sx={{ ...primaryBtnSx, whiteSpace: "nowrap", minWidth: 92 }}
                                    >
                                      {savingId === item.TimeOffRequestID ? (
                                        <CircularProgress size={18} sx={{ color: "white" }} />
                                      ) : (
                                        "Save"
                                      )}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                    
                                <TableRow>
                                  <TableCell colSpan={17} sx={{ p: 0, backgroundColor: "#fff" }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ p: 2 }}>
                                        <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                                          Leave History for {item.FullName} ({studentId})
                                        </Typography>
                                    
                                        {loadingHistoryId === item.TimeOffRequestID ? (
                                          <LinearProgress />
                                        ) : history.length === 0 ? (
                                          <Typography color="text.secondary">
                                            No previous leave history found.
                                          </Typography>
                                        ) : (
                                          <Box sx={{ overflowX: "auto" }}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={theadRowSx}>
                                                  {[
                                                    "#",
                                                    "Semester",
                                                    "Leave Type",
                                                    "Reason",
                                                    "Approved Start",
                                                    "Approved End",
                                                    "Days",
                                                    "OUT Photo",
                                                    "IN Photo",
                                                    "Comments",
                                                    "Approved By",
                                                    "Status",
                                                    "Approved Date",
                                                  ].map((h) => (
                                                    <TableCell key={h} sx={{ whiteSpace: "nowrap" }}>
                                                      {h}
                                                    </TableCell>
                                                  ))}
                                                </TableRow>
                                              </TableHead>
                                                
                                              <TableBody>
                                                {history.map((h, hidx) => (
                                                  <TableRow
                                                    key={h.TimeOffRequestID || hidx}
                                                    sx={zebraRowSx(hidx)}
                                                  >
                                                    <TableCell>{hidx + 1}</TableCell>
                                                    <TableCell>{h.Semester}</TableCell>
                                                    <TableCell>{h.LeaveType}</TableCell>
                                                    <TableCell sx={{ minWidth: 220 }}>
                                                      {h.ReasonForLeave}
                                                    </TableCell>
                                                    <TableCell>
                                                      {formatDateTimeIST(h.ApprovedLeaveStartDate)}
                                                    </TableCell>
                                                    <TableCell>
                                                      {formatDateTimeIST(h.ApprovedLeaveEndDate)}
                                                    </TableCell>
                                                    <TableCell>{h.LeaveDays}</TableCell>
                                                    <TableCell>
                                                      <AccompanyingPhotoThumb
                                                        src={h.AccompanyingPersonPhoto_OUT}
                                                        label="OUT accompanying person"
                                                      />
                                                    </TableCell>                                        
                                                    <TableCell>
                                                      <AccompanyingPhotoThumb
                                                        src={h.AccompanyingPersonPhoto_IN}
                                                        label="IN accompanying person"
                                                      />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 220 }}>
                                                      {h.Comments}
                                                    </TableCell>
                                                    <TableCell>{h.ApprovedBy}</TableCell>
                                                    <TableCell>{h.LeaveStatus}</TableCell>
                                                    <TableCell>
                                                      {formatDateTimeIST(h.ApprovedDate)}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        )}
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>
                </Box>
              </Paper>
            </Box>
          </Paper>

          {/* ✅ NEW: Active Leaves tile below (revoke) */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Current Active Leaves
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                  Approved leaves that are active right now (you can revoke them)
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  border: `1px solid ${BORDER}`,
                  backgroundColor: "#fff",
                  fontWeight: 900,
                  color: "rgba(25,118,210,0.95)",
                  whiteSpace: "nowrap",
                }}
              >
                Active: {activeLeaves.length}
              </Box>
            </Box>

            <Box sx={{ mt: 1.5, pl: 1 }}>
              <Paper elevation={0} sx={tableWrapSx}>
                <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                  <TableContainer component="div">
                    {loadingActiveLeaves ? (
                      <Typography sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                        Loading active leaves…
                      </Typography>
                    ) : activeLeaves.length === 0 ? (
                      <Typography sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                        No active leaves right now
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            {[
                              "#",
                             // "TimeOffRequestID",
                             "StudentID",
                              "Full Name",
                             // "StudentKey",
                              "Mobile",
                              "SubCampus",
                              "Approved Start",
                              "Approved End",
                              "Days",
                              "Type",
                              "Comments",
                              "Actions",
                            ].map((h) => (
                              <TableCell key={h} sx={{ whiteSpace: "nowrap" }}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeLeaves.map((r, idx) => (
                            <TableRow key={r.TimeOffRequestID} sx={zebraRowSx(idx)}>
                              <TableCell>{idx + 1}</TableCell>
                              {/*
                              <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 900 }}>
                                {r.TimeOffRequestID}
                              </TableCell>
                              */}
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{r.StudentID}</TableCell>
                              <TableCell>{r.FullName}</TableCell>
                              {/*<TableCell sx={{ whiteSpace: "nowrap" }}>{r.StudentKey}</TableCell>*/}
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{r.MobileNumber}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{r.SubCampusCode}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {formatDateTimeIST(r.ApprovedLeaveStartDate)}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {formatDateTimeIST(r.ApprovedLeaveEndDate)}
                              </TableCell>
                              <TableCell>{r.LeaveDays}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{r.LeaveType}</TableCell>
                              <TableCell sx={{ minWidth: 260 }}>
                              <TextField
                                  size="small"
                                  sx={inputSx}
                                  placeholder="Comment required"
                                  value={revokeComments[r.TimeOffRequestID] || ""}
                                  onChange={(e) => handleRevokeCommentChange(r.TimeOffRequestID, e.target.value)}
                                  fullWidth
                                  error={!((revokeComments[r.TimeOffRequestID] || "").trim())}
                                  helperText={
                                    !((revokeComments[r.TimeOffRequestID] || "").trim())
                                      ? "Required"
                                      : " "
                                  }
                                />
                              </TableCell>

                              <TableCell>
                                {/*
                                <Button
                                  variant="contained"
                                  sx={{ ...dangerBtnSx, whiteSpace: "nowrap", minWidth: 100 }}
                                  onClick={() => handleRevokeLeave(r.TimeOffRequestID)}
                                  disabled={revokeId === r.TimeOffRequestID}
                                >
                                  {revokeId === r.TimeOffRequestID ? (
                                    <CircularProgress size={18} sx={{ color: "white" }} />
                                  ) : (
                                    "Revoke"
                                  )}
                                </Button>
                                */}
                                <Button
                                  variant="contained"
                                  sx={{ ...dangerBtnSx, whiteSpace: "nowrap", minWidth: 100 }}
                                  onClick={() => handleRevokeLeave(r.TimeOffRequestID)}
                                  disabled={
                                    revokeId === r.TimeOffRequestID ||
                                    !((revokeComments[r.TimeOffRequestID] || "").trim())
                                  }
                                >
                                  {revokeId === r.TimeOffRequestID ? (
                                    <CircularProgress size={18} sx={{ color: "white" }} />
                                  ) : (
                                    "Revoke"
                                  )}
                                </Button>

                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>
                </Box>
              </Paper>
            </Box>
          </Paper>

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"}
              sx={{ borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
};

export default ApproverTimeOffRequest;
